import { prop } from 'js/utils/ramda.util';
import React, { useMemo, useCallback } from 'react';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { REPORT_TYPES } from '../constants';
import { useTransactions } from './useTransactions';
import { useDynamicReducerSelector } from '../store';
import { DownloadLink } from '../components/DownloadLink';

import style from '../W2GReportingForm.scss';

export const useTableData = () => {
    const transactions = useTransactions();
    const translate = useComponentTranslations();
    const { amount } = useDynamicReducerSelector(prop('winLoss'));
    const getDownloadLink = useCallback((id) => (
        <DownloadLink
            transactionId={id}
            translate={translate}
            reportType={REPORT_TYPES.TRANSACTION}
        />
    ), [translate]);

    const getAccordionCell = useCallback((name, value) => (
        <>
            <div className="accordionBodyCellName">{translate(name)}</div>
            <div className="accordionBodyCellValue">{value}</div>
        </>
    ), [translate]);

    return useMemo(() => {
        if (Number(amount) && !transactions.length) {
            return {};
        }

        return {
            tableView: {
                headerCells: [
                    { id: 0, content: translate('transaction.typeOfWager'), className: style.type },
                    { id: 1, content: translate('transaction.transactionId') },
                    { id: 2, content: translate('transaction.transactionDate') },
                    { id: 3, content: translate('transaction.winnings') },
                    { id: 4, content: translate('transaction.withheldTax'), className: style.tax },
                    { id: 5, content: translate('pdf') }
                ],
                rows: transactions.map(item => {
                    const {
                        winnings,
                        typeOfWager,
                        withheldTax,
                        transactionId,
                        transactionDate
                    } = item;

                    return {
                        id: transactionId,
                        rowCells: [
                            { id: 0, content: typeOfWager, className: `${style.type} ${style.highlight}` },
                            { id: 1, content: transactionId },
                            { id: 2, content: transactionDate },
                            { id: 3, content: winnings },
                            { id: 4, content: withheldTax, className: style.tax },
                            { id: 5, content: getDownloadLink(transactionId) }
                        ]
                    };
                })
            },
            accordionView: {
                rows: transactions.map(item => {
                    const {
                        winnings,
                        withheldTax,
                        typeOfWager,
                        transactionId,
                        transactionDate
                    } = item;

                    return {
                        id: transactionId,
                        headerCells: [{
                            id: 0,
                            content: typeOfWager,
                            className: `${style.highlight} accordionHeaderSmallDetails`
                        }, {
                            id: 1,
                            content: (
                                <>
                                    <span className={style.winnings}>{winnings}</span>
                                    <span>{transactionDate}</span>
                                </>
                            ),
                            className: `${style.winningsInfo} accordionHeaderSmallDetails`
                        }],
                        rowCells: [
                            { id: 0, content: getAccordionCell('transaction.transactionId', transactionId) },
                            { id: 1, content: getAccordionCell('transaction.withheldTax', withheldTax) },
                            { id: 2, content: getAccordionCell('pdf', getDownloadLink(transactionId)) }
                        ]
                    };
                })
            }
        };
    }, [amount, translate, transactions, getDownloadLink, getAccordionCell]);
};
